import { FaArrowRight } from "react-icons/fa";
import { useSlideStore } from "@/features/tutor/store/useSlideStore";
import { useSessionStore } from "@/features/tutor/store/useSessionStore";
import { useRuntimeContext } from "@/features/tutor/context/RuntimeContext.tsx";
import { useMemo, useCallback, useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  useNotificationStore,
  NotificationType,
} from "@/store/useNotificationStore";
import {
  LOCKED_TOAST,
  NOTIFICATION_STACKS,
  NotificationStackId,
} from "@/constants/notification";
import { useCooldown } from "@/features/tutor/context/CooldownContext";
import { useAuthStore } from "@/store/useAuthStore";
import { isStudent, isTeacher } from "@/utils/role-utils";
import {
  trackStudentInteraction,
  StudentInteractionTypes,
} from "@/utils/mixpanel-utils";
import { captureAndUploadSlideScreenshot } from "@/features/tutor/utils/slideScreenshot";
import { persistNotepadCheckpoint } from "@/features/notepad/components/canvasApi";
import { debugLog } from "@/utils/debugLog";
import { useIsSlideNavigationLocked } from "@/features/tutor/hooks/slideNavigationLockHook";
import { provideDevCommand } from "@/devHooks/devHookConnector";

const NextBiteButton = () => {
  const { handleChangeSlide, runManualSlideChange } = useSlideStore();
  const { sessionId } = useSessionStore();
  const { emit } = useRuntimeContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentSlideIndex = useSlideStore((state) => state.currentSlideIndex);
  const isSlideChangeInFlight = useSlideStore(
    (state) => state.pendingSlideChangeRequest !== null,
  );
  const slidesLength = useSlideStore((state) => state.slides.length);
  const { showTutorViewNotification } = useNotificationStore();
  const isNavigationLocked = useIsSlideNavigationLocked();
  const {
    isOnCooldown,
    startCooldown,
    resetCooldown,
    cooldownInfo,
    cooldownSlideIndex,
    incrementCooldownTime,
    getCurrentCooldownLabel,
  } = useCooldown();
  const { user } = useAuthStore();
  // `user.role` is a comma-separated roles string ("TEACHER,ORG_ADMIN"), so it
  // must go through role-utils — a raw `=== "TEACHER"` check misses org admins
  // and any multi-role account, leaving the button permanently locked.
  const userRole = user?.role;
  const isTeacherRole = isTeacher(userRole);
  const isStudentRole = !isTeacherRole && isStudent(userRole);
  const [isClicked, setIsClicked] = useState(false);
  const [isNotificationShowing, setIsNotificationShowing] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const prevIsOnCooldownRef = useRef(isOnCooldown);
  const prevSlideIndexRef = useRef(currentSlideIndex);
  const hasStartedInitialCooldownRef = useRef(false);
  const notificationTimeoutRef = useRef(null);
  const wasSlideChangeInFlightRef = useRef(false);

  // Start cooldown when lesson starts (on initial mount for students)
  useEffect(() => {
    if (isTeacherRole && sessionId && slidesLength > 0) {
      // Teachers and org admins don't need cooldown, just mark as initialized
      setIsInitialized(true);
    } else if (
      isStudentRole &&
      sessionId &&
      slidesLength > 0 &&
      !hasStartedInitialCooldownRef.current &&
      currentSlideIndex + 1 !== slidesLength
    ) {
      startCooldown();
      hasStartedInitialCooldownRef.current = true;
      setIsInitialized(true);
    }
  }, [
    sessionId,
    slidesLength,
    isTeacherRole,
    isStudentRole,
    startCooldown,
    currentSlideIndex,
  ]);

  // Start cooldown at the beginning of each slide (for students only). The index
  // moves optimistically, so wait for change_slide: a failed change rolls back
  // to this same bite, whose cooldown must stay as it was.
  useEffect(() => {
    if (isSlideChangeInFlight) {
      wasSlideChangeInFlightRef.current = true;
      return;
    }
    // Settled on the same bite: the change failed, free the click.
    if (
      wasSlideChangeInFlightRef.current &&
      currentSlideIndex === prevSlideIndexRef.current
    ) {
      setIsClicked(false);
    }
    wasSlideChangeInFlightRef.current = false;
    if (
      isStudentRole &&
      currentSlideIndex !== prevSlideIndexRef.current &&
      prevSlideIndexRef.current !== null &&
      currentSlideIndex + 1 !== slidesLength
    ) {
      debugLog("[NextBiteButton] Slide changed, calling startCooldown", {
        currentSlideIndex,
        prev: prevSlideIndexRef.current,
      });
      // Reset clicked state and cooldown level on every slide change
      setIsClicked(false);
      resetCooldown();
      startCooldown();
    }
    prevSlideIndexRef.current = currentSlideIndex;
  }, [
    currentSlideIndex,
    isSlideChangeInFlight,
    slidesLength,
    isStudentRole,
    startCooldown,
    resetCooldown,
  ]);

  // Reset clicked state when cooldown is reset (isOnCooldown transitions from true to false)
  useEffect(() => {
    if (prevIsOnCooldownRef.current && !isOnCooldown) {
      setIsClicked(false);
    }
    prevIsOnCooldownRef.current = isOnCooldown;
  }, [isOnCooldown]);

  // Cleanup notification timeout on unmount
  useEffect(() => {
    return () => {
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }
    };
  }, []);

  const disabled = useMemo(() => {
    // Teachers and org admins can always click Next Bite (never locked)
    if (isTeacherRole) {
      return false;
    }

    // For students and other roles, lock when:
    // - Not yet initialized (prevents brief enabled state on mount)
    // - Button was clicked and is processing (isClicked)
    // - On cooldown timer (isOnCooldown)
    // - A slide change is promised or under way (isNavigationLocked)
    // - change_slide in flight; the index already moved (isSlideChangeInFlight)
    const isDisabled =
      !isInitialized ||
      isClicked ||
      isOnCooldown ||
      isNavigationLocked ||
      isSlideChangeInFlight;

    return isDisabled;
  }, [
    isInitialized,
    isClicked,
    isOnCooldown,
    isNavigationLocked,
    isSlideChangeInFlight,
    isTeacherRole,
  ]);

  const showCooldownNotification = useCallback(() => {
    // Mark notification as showing
    setIsNotificationShowing(true);

    // Clear existing timeout if any
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
    }

    // Get the current cooldown label (dynamically calculated from remaining time)
    const currentLabel = getCurrentCooldownLabel();

    showTutorViewNotification(
      LOCKED_TOAST.title(currentLabel),
      {},
      LOCKED_TOAST.durationMs,
      LOCKED_TOAST.subheading,
      NotificationType.LOCKED,
      NOTIFICATION_STACKS[NotificationStackId.NEXT_SLIDE],
    );

    // Mark notification as hidden once it is gone
    notificationTimeoutRef.current = setTimeout(() => {
      setIsNotificationShowing(false);
      notificationTimeoutRef.current = null;
    }, LOCKED_TOAST.durationMs);
  }, [getCurrentCooldownLabel, showTutorViewNotification]);

  const handleClick = useCallback(async () => {
    if (disabled) {
      // The countdown toast is already up in this same stack saying the bite
      // is ending; "Locked for 1 minute" beside it contradicts it. A change
      // already in flight explains itself the same way.
      if (isNavigationLocked || isSlideChangeInFlight) {
        return;
      }

      // Check if student is spamming (clicking while notification is showing)
      if (isNotificationShowing && isOnCooldown && isStudentRole) {
        incrementCooldownTime(15000); // Add 15 seconds (15000ms)
      }

      // Show cooldown notification for the remaining locked states:
      // 1. During cooldown timer (isOnCooldown)
      // 2. After button clicked while processing (isClicked)
      showCooldownNotification();
      return;
    }

    // Lock the button immediately
    setIsClicked(true);

    const didNavigate = await runManualSlideChange(async () => {
      await captureAndUploadSlideScreenshot(currentSlideIndex);
      // Same checkpoint the AI-driven transition runs (slideTransition.js): the
      // tldraw snapshot is client-owned, so leaving a notepad bite without it
      // loses that bite's board for good. No-op on non-notepad slides, bounded
      // at 2s, never blocks navigation.
      await persistNotepadCheckpoint();
      emit("TURN_CHANGE");
      // Target taken from the index this click was made against: an AI transition
      // can land during the awaits above and a relative step would skip a bite.
      // handleChangeSlide notifies backend; backend decides if this is a skip
      const slideChange = handleChangeSlide(
        sessionId,
        currentSlideIndex + 1,
        "user",
      );
      trackStudentInteraction({
        type: StudentInteractionTypes.NEXT_SLIDE,
      });
      return slideChange;
    });
    // Another control took the lock between render and this click. Nothing
    // navigated, so no slide change will come along to clear isClicked.
    if (!didNavigate) setIsClicked(false);
    return didNavigate;
  }, [
    disabled,
    isClicked,
    isOnCooldown,
    isSlideChangeInFlight,
    isNavigationLocked,
    runManualSlideChange,
    isNotificationShowing,
    isStudentRole,
    incrementCooldownTime,
    showCooldownNotification,
    sessionId,
    emit,
    handleChangeSlide,
    currentSlideIndex,
  ]);

  // Dev builds only: the same click, for agents. Refused while locked rather
  // than clicked, so it never triggers the spam penalty above.
  useEffect(() => {
    if (!__DEV_OVERLAY__) return undefined;
    return provideDevCommand("tutor.nextBite", async () => {
      if (disabled) return { ok: false, error: "Next Bite is locked" };
      const didNavigate = await handleClick();
      return didNavigate
        ? { ok: true }
        : { ok: false, error: "another slide change took the lock first" };
    });
  }, [disabled, handleClick]);

  return (
    <button
      onClick={handleClick}
      title="Next Bite"
      aria-disabled={disabled}
      className={`w-[52.5px] h-[52.5px] rounded-full flex items-center justify-center focus:outline-none transition-colors duration-200 bg-[#F7F6FF] text-black ${
        disabled
          ? "opacity-50 cursor-not-allowed"
          : "can-hover:hover:text-white can-hover:hover:bg-indigo-800 active:text-white active:bg-indigo-700"
      }`}
    >
      <FaArrowRight className="w-4 h-4 text-eqblue" />
    </button>
  );
};

export default NextBiteButton;
